import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, Pencil, Trash2, Plug, PlugZap, Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/shared/DataTable';
import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { useToast } from '@/components/shared/Toast';
import {
  useGetAgentProvidersQuery,
  useCreateAgentProviderMutation,
  useUpdateAgentProviderMutation,
  useDeleteAgentProviderMutation,
  useTestAgentProviderMutation,
} from '@/store/api/agentFactoryApi';
import type { AgentProviderConfig, AgentProviderType, ProviderTestResult } from '@/types';

interface ProviderFormState {
  name: string;
  description: string;
  type: AgentProviderType;
  baseURL: string;
  /** WRITE-ONLY: only sent when the user types something. Never fetched back. */
  apiKey: string;
  organization: string;
  project: string;
  fetchTimeoutMs: string;
}

const emptyForm: ProviderFormState = {
  name: '',
  description: '',
  type: 'openai-compatible',
  baseURL: '',
  apiKey: '',
  organization: '',
  project: '',
  fetchTimeoutMs: '',
};

function fromProvider(p: AgentProviderConfig): ProviderFormState {
  return {
    name: p.name,
    description: p.description ?? '',
    type: p.type,
    baseURL: p.baseURL,
    apiKey: '',
    organization: p.organization ?? '',
    project: p.project ?? '',
    fetchTimeoutMs: p.fetchTimeoutMs != null ? String(p.fetchTimeoutMs) : '',
  };
}

export function ProvidersPage() {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const { data: providers, isLoading, isError } = useGetAgentProvidersQuery();
  const [createProvider, { isLoading: creating }] = useCreateAgentProviderMutation();
  const [updateProvider, { isLoading: updating }] = useUpdateAgentProviderMutation();
  const [deleteProvider, { isLoading: deleting }] = useDeleteAgentProviderMutation();
  const [testProvider, { isLoading: testing }] = useTestAgentProviderMutation();

  // ── Form dialog ────────────────────────────────────────────────────────────
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ProviderFormState>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<AgentProviderConfig | null>(null);

  // ── Test dialog ────────────────────────────────────────────────────────────
  const [testTarget, setTestTarget] = useState<AgentProviderConfig | null>(null);
  const [testResult, setTestResult] = useState<ProviderTestResult | null>(null);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (p: AgentProviderConfig) => {
    setEditingId(p._id);
    setForm(fromProvider(p));
    setDialogOpen(true);
  };

  const isSubmitting = creating || updating;

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.baseURL.trim()) return;
    const body = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      type: form.type,
      baseURL: form.baseURL.trim(),
      apiKey: form.apiKey || undefined,
      organization: form.organization.trim() || undefined,
      project: form.project.trim() || undefined,
      fetchTimeoutMs: form.fetchTimeoutMs ? Number(form.fetchTimeoutMs) : undefined,
    };
    try {
      if (editingId) {
        await updateProvider({ id: editingId, body }).unwrap();
        toastSuccess('Provider updated');
      } else {
        await createProvider(body).unwrap();
        toastSuccess('Provider created');
      }
      setDialogOpen(false);
    } catch (err) {
      toastError((err as any)?.data?.message || 'Failed to save provider');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteProvider(deleteTarget._id).unwrap();
      toastSuccess('Provider deleted');
      setDeleteTarget(null);
    } catch {
      toastError('Failed to delete provider');
    }
  };

  const handleTest = async (p: AgentProviderConfig) => {
    setTestTarget(p);
    setTestResult(null);
    try {
      const result = await testProvider(p._id).unwrap();
      setTestResult(result);
      if (result.ok) toastSuccess(`Connection OK (${result.latencyMs}ms)`);
    } catch (err) {
      setTestResult({ ok: false, error: (err as any)?.data?.message || 'Test failed', latencyMs: 0 });
      toastError('Provider test failed');
    }
  };

  const columns: ColumnDef<AgentProviderConfig, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Plug className="h-4 w-4 shrink-0 text-muted-foreground" />
            <div className="min-w-0">
              <p className="truncate font-medium">{row.original.name}</p>
              {row.original.description && (
                <p className="truncate text-xs text-muted-foreground">{row.original.description}</p>
              )}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'type',
        header: 'Type',
        cell: ({ row }) => <Badge variant="outline">{row.original.type}</Badge>,
      },
      {
        accessorKey: 'baseURL',
        header: 'Base URL',
        cell: ({ row }) => (
          <span className="truncate font-mono text-xs">{row.original.baseURL}</span>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => format(new Date(row.original.createdAt), 'MMM d, yyyy'),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <Button
              variant="ghost"
              size="icon"
              title="Test connection"
              disabled={testing}
              onClick={(e) => {
                e.stopPropagation();
                handleTest(row.original);
              }}
            >
              <PlugZap className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Edit"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(row.original);
              }}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              title="Delete"
              onClick={(e) => {
                e.stopPropagation();
                setDeleteTarget(row.original);
              }}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [testing],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Providers</h1>
          <p className="text-sm text-muted-foreground">
            External model provider endpoints (OpenAI-compatible / LiteLLM). API keys are
            write-only and never returned.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> New Provider
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-0">
          <p className="text-sm font-medium">Configured providers</p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner className="py-12" />
          ) : isError ? (
            <p className="py-8 text-center text-sm text-destructive">Failed to load providers.</p>
          ) : !providers || providers.length === 0 ? (
            <EmptyState
              title="No providers"
              description="Add a provider to expose models to factories."
              action={<Button onClick={openCreate}>New Provider</Button>}
            />
          ) : (
            <DataTable
              columns={columns}
              data={providers}
              onRowClick={(p) => navigate(`/models?provider=${p._id}`)}
            />
          )}
        </CardContent>
      </Card>

      {/* ── Create / edit dialog ─────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit provider' : 'New provider'}</DialogTitle>
            <DialogDescription>
              {editingId
                ? 'Update endpoint settings. Leave the API key blank to keep the stored key.'
                : 'The apiKey is encrypted at rest and shown here only when you type it.'}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="prov-name">Name *</Label>
              <Input
                id="prov-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. OpenAI"
              />
            </div>
            <div className="space-y-2">
              <Label>Type *</Label>
              <Select
                value={form.type}
                onValueChange={(v) => setForm({ ...form, type: v as AgentProviderType })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Provider type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="openai-compatible">OpenAI-compatible</SelectItem>
                  <SelectItem value="litellm">LiteLLM</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="prov-url">Base URL *</Label>
              <Input
                id="prov-url"
                value={form.baseURL}
                onChange={(e) => setForm({ ...form, baseURL: e.target.value })}
                placeholder="https://api.openai.com/v1"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prov-key">API key{!editingId ? ' *' : ''}</Label>
              <Input
                id="prov-key"
                type="password"
                autoComplete="new-password"
                value={form.apiKey}
                onChange={(e) => setForm({ ...form, apiKey: e.target.value })}
                placeholder={editingId ? 'Leave blank to keep current key' : 'sk-...'}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prov-timeout">Fetch timeout (ms)</Label>
              <Input
                id="prov-timeout"
                type="number"
                value={form.fetchTimeoutMs}
                onChange={(e) => setForm({ ...form, fetchTimeoutMs: e.target.value })}
                placeholder="60000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prov-org">Organization</Label>
              <Input
                id="prov-org"
                value={form.organization}
                onChange={(e) => setForm({ ...form, organization: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="prov-project">Project</Label>
              <Input
                id="prov-project"
                value={form.project}
                onChange={(e) => setForm({ ...form, project: e.target.value })}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="prov-desc">Description</Label>
              <Input
                id="prov-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !form.name.trim() || !form.baseURL.trim()}
            >
              {isSubmitting ? 'Saving…' : editingId ? 'Save changes' : 'Create provider'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Test connection dialog ───────────────────────────────────────── */}
      <Dialog
        open={!!testTarget}
        onOpenChange={(open) => !open && setTestTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test connection — {testTarget?.name}</DialogTitle>
            <DialogDescription>
              Live probe of {testTarget?.baseURL} (10s timeout).
            </DialogDescription>
          </DialogHeader>

          {testing ? (
            <div className="flex items-center justify-center gap-2 py-8 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Testing connection…
            </div>
          ) : testResult ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {testResult.ok ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-destructive" />
                )}
                <span className="font-medium">
                  {testResult.ok ? 'Connected' : 'Connection failed'}
                </span>
                <Badge variant="outline" className="ml-auto tabular-nums">
                  {testResult.latencyMs ?? '—'} ms
                </Badge>
              </div>
              {testResult.error && (
                <p className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs text-destructive">
                  {testResult.error}
                </p>
              )}
              {testResult.ok && (
                <div>
                  <p className="mb-1 text-xs font-medium text-muted-foreground">
                    Models discovered ({testResult.models?.length ?? 0})
                  </p>
                  {testResult.models && testResult.models.length > 0 ? (
                    <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
                      {(testResult.models as string[]).map((m) => (
                        <span
                          key={m}
                          className="rounded-full bg-muted px-2 py-0.5 font-mono text-xs"
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <JsonViewer data={testResult} initialExpanded={false} />
                  )}
                </div>
              )}
            </div>
          ) : null}

          <DialogFooter>
            <Button onClick={() => setTestTarget(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete provider"
        description={`Are you sure you want to delete "${deleteTarget?.name ?? 'this provider'}"? Models using it will be unavailable.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={handleDelete}
        isLoading={deleting}
      />
    </div>
  );
}